import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import { listCampaigns, toggleCampaignActive, downloadEmailCampaign } from "../../Services/campaign";
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { Flex, Tooltip, DatePicker, Select, Switch, Button, notification } from "antd";
import { EyeOutlined, ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import { FilterContainer } from "../../Styles/filterStyle";
import CampaignRecipientsModal from "./CampaignRecipientsModal";
import CreateCampaignModal from "./CreateCampaignModal";

const { RangePicker } = DatePicker;

const columns = (showRecipientsModal, toggleActive, isToggling) => [
  { title: "#ID", dataIndex: "id", key: "id" },
  {
    title: "Campaign Title",
    dataIndex: "title",
    key: "title",
    render: (text) =>
      text && text.length > 40 ? (
        <Tooltip title={text}>{text.slice(0, 40) + "..."}</Tooltip>
      ) : (
        text
      ),
  },
  {
    title: "Itineraries",
    dataIndex: "itinerary_count",
    key: "itinerary_count",
    render: (count) => (count === 1 ? "1 itinerary" : `${count || 0} itineraries`),
  },
  { title: "Sent Count", dataIndex: "sent_count", key: "sent_count" },
  { title: "Download Count", dataIndex: "download_count", key: "download_count" },
  { title: "Useful Count", dataIndex: "useful_count", key: "useful_count" },
  { title: "Not Useful Count", dataIndex: "not_useful_count", key: "not_useful_count" },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (text) => (text ? formatDate(text) : "-"),
  },
  {
    title: "Active",
    dataIndex: "is_active",
    key: "is_active",
    render: (val, record) => (
      <Switch
        checked={val}
        loading={isToggling}
        onChange={(checked) => toggleActive(record.id, checked)}
      />
    ),
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    render: (_, record) => (
      <Tooltip title="View Recipients">
        <Button icon={<EyeOutlined />} type="link" onClick={() => showRecipientsModal(record)} />
      </Tooltip>
    ),
  },
];

// `tabs` (Itinerary Email | Custom Email) is rendered under the page heading and buttons.
function EmailCampaignList({ tabs }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [dateRange, setDateRange] = useState({ start_date: null, end_date: null });
  const [isRecipientsModalVisible, setIsRecipientsModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter, dateRange]);

  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["email_campaigns", currentPage, pageSize, searchTerm, activeFilter, dateRange],
    queryFn: () =>
      listCampaigns({
        skip,
        limit: pageSize,
        search: searchTerm,
        is_active: activeFilter,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange({
        start_date: dates[0].format("YYYY-MM-DD"),
        end_date: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      setDateRange({ start_date: null, end_date: null });
    }
  };

  const { mutate: mutateToggle, isPending: isToggling } = useMutation({
    mutationFn: ({ campaign_id, is_active }) => toggleCampaignActive({ campaign_id, is_active }),
    onSuccess: () => {
      notification.success({ message: "Campaign status updated" });
      queryClient.invalidateQueries({ queryKey: ["email_campaigns"] });
    },
  });

  const toggleActive = (campaignId, checked) => {
    mutateToggle({ campaign_id: campaignId, is_active: checked });
  };

  const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
    mutationFn: async () => {
      return await downloadEmailCampaign({
        search: searchTerm,
        is_active: activeFilter,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      });
    },
  });

  const showRecipientsModal = (record) => {
    setSelectedCampaign(record);
    setIsRecipientsModalVisible(true);
  };

  return (
    <div>
      <Flex justify="space-between" align="center">
        <h3>Email Campaigns</h3>
        <Flex gap="middle">
          <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
            Create Campaign
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={triggerDownload} loading={isDownloading}>
            Export
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
        </Flex>
      </Flex>
      {tabs || <br />}
      <FilterContainer>
        <DebouncedInput
          placeholder="Search by Campaign Title"
          value={searchTerm}
          onChange={setSearchTerm}
          delay={1000}
        />
        <RangePicker onChange={handleDateChange} disabledDate={(current) => current && current.toDate() > new Date()} />
        <Select
          value={activeFilter}
          onChange={(val) => setActiveFilter(val)}
          allowClear
          placeholder="Status"
          style={{ width: "150px" }}
          options={[
            { label: "Active", value: true },
            { label: "Inactive", value: false },
          ]}
        />
      </FilterContainer>

      <CustomTable
        columns={columns(showRecipientsModal, toggleActive, isToggling)}
        data={data?.campaigns || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight={"calc(90vh - 245px)"}
      />

      <CampaignRecipientsModal
        isModalVisible={isRecipientsModalVisible}
        handleCancel={() => setIsRecipientsModalVisible(false)}
        campaignData={selectedCampaign}
      />
      <CreateCampaignModal
        isModalVisible={isCreateModalVisible}
        handleCancel={() => setIsCreateModalVisible(false)}
        handleOk={() => {
          setIsCreateModalVisible(false);
          refetch();
        }}
      />
    </div>
  );
}

export default EmailCampaignList;
